import { PartnershipType } from '../../../../../types';
import {
  ClientType,
  InsurerType,
  InternalHoldBaseType,
  PaginatedBillType,
  PaginatedSubmissionType,
  UnbilledBillType,
} from '../../../types';

import type { AutocompleteSelectOption } from '../../grid-components/grid-types';

export {
  ActionTypeDisplayText,
  GridLevelName,
} from '../../grid-components/grid-types';

export enum ReleasableHoldReasons {
  CARRIER_CHANGE = 'carrier_change',
  CLIENT_CHANGE = 'client_change',
  MANUAL_HOLD = 'manual_hold',
}

export enum NonReleasableHoldReasons {
  PARTNERSHIP_CHANGE = 'partnership_change',
  EXCEEDED_CLIENT_CAP = 'exceeded_client_cap',
  EXCEEDED_USER_CAP = 'exceeded_user_cap',
  LOSS_OF_COVERAGE = 'loss_of_coverage',
  MISSING_DATA = 'missing_data',
  MISSING_PRICE_INFO = 'missing_price_info',
  MISSING_PRIMARY_SUBSCRIBER = 'missing_primary_subscriber',
  REMOVED_FROM_PROGRAM = 'removed_from_program',
  SEQUENTIAL_SUBMISSION_HOLD = 'sequential_submission_hold',
  MISSING_CPT_CODE = 'missing_cpt_code',
  UNEXPECTED_PRICE = 'unexpected_price',
  ELIGIBILITY_SERVICE_ERROR = 'eligibility_service_error',
}

export enum EligibilityCheckHoldReasons {
  LOSS_OF_COVERAGE = 'loss_of_coverage',
  ELIGIBILITY_SERVICE_ERROR = 'eligibility_service_error',
  CLIENT_CHANGE = 'client_change',
  CARRIER_CHANGE = 'carrier_change',
  PARTNERSHIP_CHANGE = 'partnership_change',
}

export const allHoldTypeVals = (): string[] => {
  const releaseVals = Object.values(ReleasableHoldReasons);
  const archiveVals = Object.values(NonReleasableHoldReasons);
  const allHoldTypeVals = [...releaseVals, ...archiveVals];
  return allHoldTypeVals;
};

export interface ClientInsurerPartnershipData {
  clientsInsurerId: number;
  client: {
    id: number | null;
    name: string | null;
    loading: boolean;
    isErrored: boolean;
  };
  insurer: {
    id: number | null;
    name: string | null;
    loading: boolean;
    isErrored: boolean;
  };
  partnership: {
    id: number | null;
    name: string | null;
  };
  billingType: {
    value: string | null;
  };
  contractDateRange: {
    value: string | null;
  };
}

export type BillTypeData = PaginatedBillType & ClientInsurerPartnershipData;
export type BillTypeDataList = BillTypeData[];

export type SubmissionTypeData = PaginatedSubmissionType &
  ClientInsurerPartnershipData;
export type SubmissionTypeDataList = SubmissionTypeData[];

export type UnbilledTypeData = UnbilledBillType & ClientInsurerPartnershipData;
export type UnbilledTypeDataList = UnbilledTypeData[];

/**
 *  maps to grid header display names
 * */
export enum GridColumnHeaderNames {
  CLAIM_ID = 'Claim ID',
  HOLD_REASON = 'Hold Reason',
  BILL_AMOUNT = 'Bill Amount',
  USER_ID = 'User ID',
  SUBSCRIPTION_ID = 'Source / ID',
  DATE_OF_SERVICE = 'Date of Service',
  CLIENT = 'Client',
  INSURER = 'Insurer',
  PARTNERSHIP = 'Partnership',
  BILL_TYPE = 'Bill Type',
  NETSUITE_ID = 'NetSuite ID',
  TRANSACTION_ID = 'Transaction ID',
  SUBMISSION = 'Submitted On / Amount',
  SUBMISSION_STATUS = 'Submission Status',
  SUBMISSION_ID = 'Submission ID',
  BILL_ID = 'Bill ID',
}

/**
 * maps to data field that grid uses to populate grid data
 * When adding new fields,
 * make sure to add them to the `billManagementColumns` array
 */
export enum GridColumnFields {
  CLAIM_ID = 'claimId',
  HOLD_REASON = 'holdDetails',
  AMOUNT = 'billAmount',
  USER_ID = 'userId',
  SOURCE = 'source',
  SOURCE_ID = 'sourceId',
  DATE_OF_SERVICE = 'dateOfService',
  CLIENT = 'client',
  INSURER = 'insurer',
  PARTNERSHIP = 'partnership',
  BILLING_TYPE = 'billingType',
  NETSUITE_ID = 'netsuiteId',
  TRANSACTION_ID = 'transactionId',
  SUBMISSION = 'submission',
  SUBMISSION_STATUS = 'submissionStatus',
  ACTIONS = 'actions',
  PAYMENT_TYPE = 'paymentType',
  SUBMISSION_ID = 'submissionId',
  BILL_ID = 'billId',
}

/**
 * UI filtering for bill management grid
 * Shape of filters in the ui
 */
export interface BillManagementGridFilterModelType {
  userIds: number[];
  source: string | null;
  sourceIds: number[];
  startDate: string | null;
  endDate: string | null;
  clientIds: number[];
  insurerIds: number[];
  partnershipIds: number[];
  held: boolean;
  submitted: boolean;
  holdReasons: string[];
  billingTypes: string[];
  submissionStatusTypes: string[];
  paymentTypes: string[];
  billIds: number[];
  sourceStartDate: string | null;
  sourceEndDate: string | null;
}

export interface RawBillManagementAdvancedFiltersValues {
  holdReasons: AutocompleteSelectOption[] | [];
  userIds: number[] | [];
  source: string | null;
  sourceIds: number[] | [];
  dateRange: (Date | null)[];
  clients: ClientType[] | [];
  insurers: InsurersType[] | [];
  partnerships: PartnershipType[] | [];
  billingTypes: AutocompleteSelectOption[] | [];
  billStatus: BillStatusType;
  submissionStatusTypes: AutocompleteSelectOption[] | [];
  paymentTypes: AutocompleteSelectOption[] | [];
  billIds: number[] | [];
  sourceDateRange: (Date | null)[];
}

export interface SubmissionManagementGridFilterModelType {
  submissionIds: number[];
  billIds: number[];
  userIds: number[];
  transactionIds: string[];
  netsuiteIds: string[];
  creditMemoIds: string[];
  submissionStatuses: string[];
  source: string | null;
  sourceIds: number[];
  sourceStartDate: string | null;
  sourceEndDate: string | null;
  clientIds: number[];
  insurerIds: number[];
  partnershipIds: number[];
  billingTypes: string[];
  submissionStartDate: string | null;
  submissionEndDate: string | null;
}

export interface BillStatusType {
  held: boolean;
  submitted: boolean;
}

export type InsurersType = Pick<InsurerType, 'id' | 'name' | 'identifier'>;

export const BILL_DATE_FORMAT = 'YYYY-MM-DD';

export interface HoldType
  extends Partial<
    Omit<InternalHoldBaseType, '__typename' | 'billId' | 'createdAt'>
  > {
  billId?: number;
  createdAt?: string | null;
  clearedAt?: string | null;
  details?: Record<string, unknown>;
  holdType: string;
  note?: string | null;
  // ACCOUNTS-AND-PLAN-MIGRATION: clientId is the hold change-to target identity, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
  clientId?: number | null;
}

export enum CostShareStatus {
  AWAITING_REMIT = 'awaiting_remit',
}
